import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import {
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TABLE_COLUMN, TableDimension, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableAxisCell from 'notebook/tables/core-ui/TableAxisCell';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import UnitsTableAxisCell from 'notebook/tables/core-ui/UnitsTableAxisCell';
import {AxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {ElementMouseEvent} from 'util/Types';

export interface TableHeaderRowProps
  // The rendering slice is defined on TableRow; pick it off rather than repeating it here.
  extends Pick<TableRowProps, 'sliceStart'>, MediocreSearchResultProps {
  // Rendering slice (see TableRow.js)
  sliceEnd: number | null;

  // Table actions
  axisCellDragOverHandler?: (index: TableIndex, e: ElementMouseEvent) => void;
  axisCellDragStartHandler?: (options: {start: TableIndex; active: string}, e: ElementMouseEvent) => void;
  axisCellDragHandleMouseDown?: (dim: TableDimension, index: number) => boolean;
  finalizeAndExitEditMode?: () => void;
  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
}

/**
 * The header row in a TableEditable, containing the maximize icon and Excel column labels (A, B, C, ...)
 */
function TableHeaderRow({
  sliceStart,
  searchResultsTree,
  selectedSearchResultTree,
  sliceEnd,
  axisCellDragOverHandler,
  axisCellDragStartHandler,
  axisCellDragHandleMouseDown,
  finalizeAndExitEditMode,
  freezeThroughColumn,
  unfreezeFromColumn,
}: TableHeaderRowProps): JSX.Element | null {
  // TODO(BNCH-166536): Remove selecting the table and instead only select the pieces necessary.
  const {tableModel: table} = useTableModel((model) => model.props.table);
  // Table-level configuration is identical for every column header, so it is read from the table
  // model rather than threaded through props by TableEditable._getHeaderTRProps. Only the
  // `columnHeader` slice is subscribed to so that unrelated model changes do not re-render the
  // header row.
  const {tableModel: columnHeader} = useTableModel((model) => model.columnHeader);
  // The leading cell's shadow is table-level; the per-column consequences of the affixed
  // configuration are derived on each column header's own model.
  const {tableModel: affixed} = useTableModel((model) => model.affixed);
  const {tableModel: firstColumnWidth} = useTableModel((model) => model.measurements.firstColumnWidth);
  const {ghostCellsCount, isLeftAlignRowHeader} = columnHeader;

  function renderCell(j: number): JSX.Element {
    const isGhostCell = computeIsAxisGhostCell(TABLE_COLUMN, j, table.table[0].length);
    const CellComponent = getCellComponent(table, j);

    return (
      <AxisCellModelProvider key={`axis-${j}`} dim={TABLE_COLUMN} index={j}>
        <CellComponent
          {...(!isGhostCell && {
            cellDragStart: axisCellDragStartHandler,
            cellDragOver: axisCellDragOverHandler,
            dragHandleMouseDown: axisCellDragHandleMouseDown,
          })}
          dataTestAttrs={{component: 'TableHeaderRow', element: 'columnLabelCell'}}
          finalizeAndExitEditMode={finalizeAndExitEditMode}
          freezeThroughColumn={freezeThroughColumn}
          unfreezeFromColumn={unfreezeFromColumn}
          {...getSearchResultPropsForIndex({searchResultsTree, selectedSearchResultTree}, j)}
        />
      </AxisCellModelProvider>
    );
  }

  function renderLeadingCell(): JSX.Element | string {
    if (table._rowHeader == null) {
      return (
        <td
          key="left-col-filter"
          className={classNames(
            'mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header cell-sticky',
            {
              'align-left': isLeftAlignRowHeader,
              'cell-rightShadow': affixed.showAffixedShadow && affixed.affixedColumnCount === 0,
            }
          )}
        />
      );
    }
    return table._rowHeader;
  }

  return (
    <tr className={classNames({'mediocre-tableEditable-headerRow--noAxis': firstColumnWidth === 0})}>
      {TableUtil.mapColumns({
        sliceStart,
        sliceEnd,

        ghostCellsCount,
        normalCellsCount: table.table[0].length,

        mapLeadingCell: () => renderLeadingCell(),
        mapNormalCell: (j: number) => renderCell(j),
        mapGhostCell: (j: number) => renderCell(j),
      })}
    </tr>
  );
}

export function getCellComponent(table: Table, j: number): typeof TableAxisCell | typeof UnitsTableAxisCell {
  switch (TableUtil.getColumnDefinitionType(table, j)) {
    case TableColumnType.QUANTITY:
    case TableColumnType.CONCENTRATION:
      return UnitsTableAxisCell;
    default:
      return TableAxisCell;
  }
}

export default React.memo(
  TableHeaderRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    // TODO(BNCH-113857): Switch back to shallow equal once redux is enabled
    Object.keys(nextProps).every((key) => isEqual(prevProps[key], nextProps[key]))
);
